import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  BodyCaption,
  BodySecondary,
  Button,
  COLORS,
  TitleSmall,
} from "@bikdotai/bik-component-library";
import { useAppSelector } from "../../state/store";
import { homeDataAction } from "../../state/homeState";
import { SideModalStyles } from "../../ui-components/side-modal/style";
import { ConfirmationModal } from "../../ui-components/confirmation-modal";
import {
  JourneyVersion,
  VersionDiff,
  VersionSource,
} from "../../services/journeyEditor/journeyEditorModels";
import { useVersionHistory } from "./useVersionHistory";
import {
  DiffBox,
  PanelHeader,
  RowLine,
  SourceBadge,
  UndoList,
  VersionRow,
} from "./style";

const SOURCE_LABELS: { [source in VersionSource]: string } = {
  ui: "Builder",
  mcp: "Claude",
  restore: "Restore",
  baseline: "Before history",
};

/**
 * The journey's saved versions, for Bik staff only: who saved each one, from where, and a
 * way back to it. Restoring writes the old version as a new one, so nothing is lost.
 */
const VersionHistoryPanel = () => {
  const dispatch = useDispatch();
  const homeState = useAppSelector((state) => state.homeState);
  const { storeId, flowId, currentVersionId } = homeState;
  const history = useVersionHistory(storeId, flowId);
  const [selected, setSelected] = useState<JourneyVersion | null>(null);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [restoring, setRestoring] = useState<boolean>(false);
  const [restoreError, setRestoreError] = useState<string>("");

  // A save in this tab, an activation included, makes a new current version; reload so a
  // restore is based on it rather than on the one the panel opened with.
  const { reload } = history;
  const openedWithVersion = useRef(currentVersionId);
  useEffect(() => {
    if (currentVersionId && currentVersionId !== openedWithVersion.current) {
      openedWithVersion.current = currentVersionId;
      setSelected(null);
      reload();
    }
  }, [currentVersionId, reload]);

  const close = () =>
    dispatch(homeDataAction.updateState({ showVersionHistory: false }));

  const select = (version: JourneyVersion) => {
    setSelected(version);
    setRestoreError("");
    history.loadDiff(version.version_id);
  };

  const restore = async () => {
    if (!selected) {
      return;
    }
    setRestoring(true);
    try {
      const result = await history.restore(
        storeId,
        flowId,
        selected.version_id,
        currentVersionId ?? history.versions[0]?.version_id ?? null,
        homeState.userData?.email ?? ""
      );
      if (result.conflict) {
        setRestoreError(
          `${result.conflict.changedBy || "Someone"} saved this journey since you opened it. Reload, then restore again.`
        );
        return;
      }
      // The canvas in this tab still holds the journey as it was; reload so the next Save
      // starts from the restored version rather than writing the old one back.
      window.location.reload();
    } catch (err: any) {
      setRestoreError(err?.message || "Could not restore the version");
    } finally {
      setRestoring(false);
      setConfirming(false);
    }
  };

  const latestVersionId = history.versions[0]?.version_id;

  return (
    <SideModalStyles isOpen>
      <PanelHeader>
        <TitleSmall>Version history</TitleSmall>
        <Button
          buttonText="Close"
          buttonType="tertiary"
          size="small"
          onClick={close}
        />
      </PanelHeader>

      {history.error && (
        <BodySecondary style={{ color: COLORS.content.negative }}>
          {history.error}
        </BodySecondary>
      )}
      {!history.isLoading &&
        !history.error &&
        history.versions.length === 0 && (
          <BodySecondary>
            No versions yet. The first one is recorded on the next save.
          </BodySecondary>
        )}

      {history.versions.map((version) => {
        const isSelected = selected?.version_id === version.version_id;
        const isLatest = version.version_id === latestVersionId;
        return (
          <VersionRow
            key={version.version_id}
            selected={isSelected}
            onClick={() => select(version)}
          >
            <RowLine>
              <BodySecondary>{formatTime(version.created_at)}</BodySecondary>
              <SourceBadge>
                {isLatest ? "Current" : SOURCE_LABELS[version.source]}
              </SourceBadge>
            </RowLine>
            <BodyCaption style={{ color: COLORS.content.secondary }}>
              {authorOf(version)}
            </BodyCaption>
            {summaryOf(version, history.versions) && (
              <BodyCaption style={{ marginTop: 4 }}>
                {summaryOf(version, history.versions)}
              </BodyCaption>
            )}

            {isSelected && !isLatest && (
              <DiffBox>
                {history.isDiffLoading && <BodyCaption>Comparing…</BodyCaption>}
                {history.diff && <UndoSummary diff={history.diff} />}
                {restoreError && (
                  <BodyCaption style={{ color: COLORS.content.negative }}>
                    {restoreError}
                  </BodyCaption>
                )}
                {!isLatest && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      buttonText="Restore this version"
                      buttonType="primary"
                      size="small"
                      onClick={(event: any) => {
                        event?.stopPropagation?.();
                        setConfirming(true);
                      }}
                    />
                  </div>
                )}
              </DiffBox>
            )}
          </VersionRow>
        );
      })}

      {history.hasMore && (
        <Button
          buttonText={history.isLoading ? "Loading…" : "Load older versions"}
          buttonType="tertiary"
          size="small"
          onClick={history.loadMore}
        />
      )}

      <ConfirmationModal
        open={confirming}
        title="Restore this version?"
        subTitle="The journey's blocks go back to this version, saved as a new version, so nothing is lost. Whether the journey is live does not change; if it is live, customers get this version straight away. Its triggers stay as they are now, but the Starting Point's connections and its ask-for-phone setting go back to this version, so check them after restoring. Unsaved changes on the canvas are lost."
        positiveButtonText="Restore"
        negativeButtonText="Cancel"
        loading={restoring}
        onPositiveClick={restore}
        onNegativeClick={() => setConfirming(false)}
        onCloseClick={() => setConfirming(false)}
      />
    </SideModalStyles>
  );
};

// A version's own line in the list: what that save did, as the server summarised it.
const summaryOf = (version: JourneyVersion, versions: JourneyVersion[]) => {
  if (version.source === "baseline") {
    return "The journey as it was before its first save here";
  }
  if (version.source === "restore") {
    const restoredId = version.message.replace(/^Restored /, "");
    const restored = versions.find((v) => v.version_id === restoredId);
    return restored
      ? `Restored the version from ${formatTime(restored.created_at)}`
      : "Restored an earlier version";
  }
  return version.message;
};

const authorOf = (version: JourneyVersion) =>
  version.source === "baseline"
    ? "Before version history"
    : version.author_email || "Unknown";

const formatTime = (millis: number) =>
  new Date(millis).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const UNDO_LIMIT = 6;

/** What restoring this version does to the journey as it is now, one step per line. */
const UndoSummary = ({ diff }: { diff: VersionDiff }) => {
  const connection = (edge: { source_name: string; target_name: string }) =>
    `${edge.source_name} → ${edge.target_name}`;
  const edgesAdded = diff.edges_added ?? [];
  const edgesRemoved = diff.edges_removed ?? [];
  const undoneNodes = diff.changed_nodes
    // A card whose exits alone changed is already told by its connection.
    .filter((node) => !node.connection_only)
    .map((node) => `Undo the changes to ${node.name}`);
  const steps = Array.from(
    new Set(
      diff.added_nodes
        .map((node) => `Remove ${node.name}`)
        .concat(
          diff.removed_nodes.map((node) => `Bring back ${node.name}`),
          edgesAdded.map((edge) => `Disconnect ${connection(edge)}`),
          edgesRemoved.map((edge) => `Reconnect ${connection(edge)}`),
          undoneNodes
        )
    )
  );
  if (!steps.length) {
    return <BodyCaption>Same as the journey now.</BodyCaption>;
  }
  const more = steps.length - UNDO_LIMIT;
  return (
    <>
      <BodyCaption>Restoring this version will:</BodyCaption>
      <UndoList>
        {steps.slice(0, UNDO_LIMIT).map((step) => (
          <li key={step}>
            <BodyCaption>{step}</BodyCaption>
          </li>
        ))}
        {more > 0 && (
          <li>
            <BodyCaption>and {more} more</BodyCaption>
          </li>
        )}
      </UndoList>
    </>
  );
};

export default VersionHistoryPanel;
